import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../components/UserContext';
import RegistrationForm from './RegistrationForm';
import styles from '../css/modules/PurchaseRegistration.module.css';

const PurchaseRegistration = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [plan, setPlan] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [isDiscountLocked, setIsDiscountLocked] = useState(false);
  const [showDiscountInput, setShowDiscountInput] = useState(false);

  const [selectAll, setSelectAll] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [acceptPartners, setAcceptPartners] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { user } = useUser();
  const isLoggedIn = !!user;

  useEffect(() => {
    if (isLoggedIn && user.email) setEmail(user.email);
  }, [isLoggedIn, user]);

  const raw = process.env;
  const plans = [1,2,3].map(i=>({
    days: parseInt(raw[`REACT_APP_PLAN${i}_DAYS`],10)||0,
    price: parseFloat(raw[`REACT_APP_PLAN${i}_PRICE`])||0,
    desc: raw[`REACT_APP_PLAN${i}_DESC`]||''
  }));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlDays = params.get('days');
    if (urlDays && plans.some(p=>p.days.toString()===urlDays)) setPlan(urlDays);
  }, [location.search, plans]);

  useEffect(() => {
    if (!isLoggedIn) {
      setAcceptTerms(selectAll);
      setAcceptMarketing(selectAll);
      setAcceptPartners(selectAll);
    }
  }, [selectAll, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) setSelectAll(acceptTerms && acceptMarketing && acceptPartners);
  }, [acceptTerms, acceptMarketing, acceptPartners, isLoggedIn]);

  const generateSessionId = () => 'sesja'+Date.now()+'_'+Math.random().toString(36).substr(2,9);
  const getBasePrice = d=> (plans.find(p=>p.days.toString()===d)?.price||0);
  const getFinalPrice = ()=> {
    const base = getBasePrice(plan);
    return discountInfo?.discountPercentage
      ? (base - base*discountInfo.discountPercentage/100).toFixed(2)
      : base.toFixed(2);
  };

  const handleDiscountCheck = async e => {
    e.preventDefault();
    setIsError(false);
    setMessage('');
    try {
      const { data } = await axios.get(`${API_BASE_URL}/cyber/check-discount?code=${discountCode}`);
      if (data.success) {
        setDiscountInfo(data);
        setMessage(`Kod rabatowy zaakceptowany. Zniżka: ${data.discountPercentage}%`);
        setIsDiscountLocked(true);
      } else {
        setDiscountInfo(null);
        setMessage('Niepoprawny lub wygasły kod rabatowy.');
        setIsError(true);
      }
    } catch {
      setMessage('Błąd podczas sprawdzania kodu rabatowego.');
      setIsError(true);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsError(false);
    setMessage('');

    if (!plan) {
      setMessage('Proszę wybrać plan dostępu.');
      setIsError(true);
      return;
    }

    if (!isLoggedIn) {
      if (password.length < 4) {
        setMessage('Hasło musi mieć co najmniej 4 znaki.');
        setIsError(true);
        return;
      }
      if (password !== confirmPassword) {
        setMessage('Hasła nie są takie same.');
        setIsError(true);
        return;
      }
      if (!acceptTerms) {
        setMessage('Musisz zaakceptować regulamin.');
        setIsError(true);
        return;
      }
      try {
        const regRes = await axios.post(`${API_BASE_URL}/cyber/register`, {
          name, email, password,
          promo_notifications: acceptMarketing,
          acceptPartners,
          website: process.env.REACT_APP_WEBSITE || 'strzelasz'
        });
        if (regRes.data.error) {
          setMessage(regRes.data.error);
          setIsError(true);
          return;
        }
        if (regRes.data.token) localStorage.setItem('access_token', regRes.data.token);
      } catch {
        setMessage('Błąd rejestracji.');
        setIsError(true);
        return;
      }
    }

    const paymentData = {
      sessionId: generateSessionId(),
      amount: parseFloat(getFinalPrice()),
      email,
      plan,
      description: "Strzelasz - Testy na patent strzelecki",
      discountCode: discountCode.trim() || null,
      website: process.env.REACT_APP_WEBSITE || 'strzelasz',
      websiteUrl: process.env.REACT_APP_WEBSITE_URL
    };
    

    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/init_payment`, paymentData);
      if (data.status==="success" && data.payment_url) window.location.href = data.payment_url;
      else throw new Error();
    } catch (err) {
      const errMsg = err.response?.data?.error
                   || err.response?.data?.message
                   || 'Błąd płatności';
      setMessage(errMsg);
      setIsError(true);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.overlay}></div>
      <div className={styles.content}>
        <h1>{isLoggedIn ? 'Zakup Dostępu' : 'Rejestracja i Zakup'}</h1>
        <form onSubmit={handleSubmit}>
          {!isLoggedIn && (
            <RegistrationForm
              name={name} setName={setName}
              email={email} setEmail={setEmail}
              password={password} setPassword={setPassword}
              confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
              selectAll={selectAll} setSelectAll={setSelectAll}
              acceptTerms={acceptTerms} setAcceptTerms={setAcceptTerms}
              acceptMarketing={acceptMarketing} setAcceptMarketing={setAcceptMarketing}
              acceptPartners={acceptPartners} setAcceptPartners={setAcceptPartners}
            />
          )}
  
          {plan && (
            <div className={styles.planSummaryBox}>
              <p>
                {(() => {
                  const selected = plans.find(p => p.days.toString() === plan);
                  return selected
                    ? `${selected.price} zł – ${selected.days} dni – ${selected.desc}`
                    : 'Nieprawidłowy plan';
                })()}
              </p>
            </div>
          )}
  
          {plan && (
            <div className={styles.priceSummary}>
              <p><strong>Kwota do zapłaty: {getFinalPrice()} zł</strong></p>
            </div>
          )}
  
          {!showDiscountInput && (
            <button
              type="button"
              className={styles.showDiscountToggle}
              onClick={() => setShowDiscountInput(true)}
            >
              Mam kod rabatowy
            </button>
          )}
  
          <div className={`${styles.discountContainer} ${showDiscountInput ? styles.active : ''}`}>
            <input
              type="text"
              id="discountCode"
              placeholder="Kod rabatowy"
              value={discountCode}
              onChange={e => setDiscountCode(e.target.value)}
              readOnly={isDiscountLocked}
            />
            <button
              onClick={handleDiscountCheck}
              disabled={isDiscountLocked || !discountCode.trim()}
              className={styles.discountButton}
            >
              Sprawdź
            </button>
          </div>
  
          <button className={styles.payButton} type="submit">ZAPŁAĆ</button>
          {message && <p className={isError ? styles.errorMessage : ''}>{message}</p>}
        </form>
      </div>
    </div>
  );
};

export default PurchaseRegistration;
